
import React, { useState } from 'react';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface Automat {
  id: string;
  name: string;
  location: string;
}

interface FixedCostFormProps {
  automaten: Automat[];
  onSave: (groups: any[]) => void;
}

const costTypes = [
  { value: 'fixkosten', label: 'Fixkosten' },
  { value: 'fixkosten-einnahme', label: 'Fixkosten Einnahme' },
  { value: 'variable-kosten', label: 'Variable Kosten' },
  { value: 'sonstiges', label: 'Sonstiges' }
];

const intervals = [
  { value: 'monatlich', label: 'Monatlich' },
  { value: 'jährlich', label: 'Jährlich' },
  { value: 'einmalig', label: 'Einmalig' }
];

const categories = [
  'Miete & Nebenkosten',
  'Strom & Energie',
  'Internet & Telefon',
  'Versicherungen',
  'Wartung & Reparaturen',
  'Marketing',
  'Bürokosten',
  'Steuerberatung',
  'Sonstige'
];

export default function FixedCostForm({ automaten, onSave }: FixedCostFormProps) {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    automatId: '',
    name: '',
    costType: 'fixkosten',
    category: '',
    interval: 'monatlich',
    description: '',
    amount: '',
    revenuePercentage: '',
    revenueAmount: '',
    notes: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: ''
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      automatId: '',
      name: '',
      costType: 'fixkosten',
      category: '',
      interval: 'monatlich',
      description: '',
      amount: '',
      revenuePercentage: '',
      revenueAmount: '',
      notes: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Bitte geben Sie einen Namen für die Fixkosten ein.');
      return;
    }

    setIsSaving(true);

    try {
      // KOMPLETT ÜBERARBEITET: Viel strengere Automaten-Validierung
      let selectedAutomat = null;
      let automatName = 'Allgemeine Fixkosten';
      
      if (formData.automatId && formData.automatId.trim() !== '') {
        console.log('🔍 Searching for automat with ID:', formData.automatId);
        console.log('🔍 Available automaten:', automaten.map(a => ({ id: a?.id, name: a?.name, valid: !!(a && a.id && a.name) })));
        
        // Sehr strenge Validierung: Alle Eigenschaften müssen vorhanden sein
        selectedAutomat = automaten.find(a => {
          if (!a) {
            console.log('❌ Null/undefined automat found');
            return false;
          }
          if (!a.id) {
            console.log('❌ Automat without ID:', a);
            return false;
          }
          if (!a.name) {
            console.log('❌ Automat without name:', a);
            return false;
          }
          if (a.id !== formData.automatId) {
            return false;
          }
          console.log('✅ Valid automat found:', a);
          return true;
        });
        
        if (!selectedAutomat) {
          console.error('❌ AUTOMAT NOT FOUND! Resetting selection...');
          console.error('❌ Searched ID:', formData.automatId);
          console.error('❌ Available IDs:', automaten.map(a => a?.id).filter(Boolean));
          
          // Automatische Bereinigung bei ungültigem Automaten
          alert('⚠️ Der ausgewählte Automat existiert nicht mehr und wurde auf "Allgemeine Fixkosten" zurückgesetzt.');
          setFormData(prev => ({ ...prev, automatId: '' }));
          automatName = 'Allgemeine Fixkosten';
        } else {
          automatName = selectedAutomat.name;
          console.log('✅ Using automat:', automatName);
        }
      } else {
        console.log('ℹ️ No automat selected, using general costs');
      }

      const isRevenue = formData.costType === 'fixkosten-einnahme';
      
      let category: 'aktive-fixkosten' | 'einnahmen' | 'umsatzbeteiligung' | 'beendet' = 'aktive-fixkosten';
      if (isRevenue) category = 'einnahmen';
      if (formData.revenuePercentage || formData.revenueAmount) category = 'umsatzbeteiligung';

      const fixedCostData = {
        user_id: user?.id,
        automat_id: selectedAutomat?.id || null,
        automat_name: automatName,
        name: formData.name,
        category: category,
        cost_type: formData.costType,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        interval: formData.interval,
        start_date: formData.startDate,
        end_date: formData.endDate || null,
        description: formData.description,
        is_revenue: isRevenue,
        revenue_percentage: formData.revenuePercentage ? parseFloat(formData.revenuePercentage) : null,
        revenue_amount: formData.revenueAmount ? parseFloat(formData.revenueAmount) : null,
        notes: formData.notes,
        status: 'aktiv'
      };

      console.log('💾 Saving fixed cost:', fixedCostData);

      const { data, error } = await supabase
        .from('fixkosten')
        .insert([fixedCostData])
        .select();

      if (error) {
        console.error('❌ Error saving fixed cost:', error);
        throw error;
      }

      console.log('✅ Successfully saved fixed cost:', data);

      // Call parent onSave to update the parent component
      onSave(data);
      
      // Reset form
      resetForm();

      alert('✅ Fixkosten wurde erfolgreich hinzugefügt!');

    } catch (error) {
      console.error('❌ Error saving fixed cost:', error);
      alert('❌ Fehler beim Speichern der Fixkosten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSaving(false);
    }
  };

  const getCostTypeColor = (costType: string) => {
    switch (costType) {
      case 'fixkosten-einnahme':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'fixkosten':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'variable-kosten':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'sonstiges':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // KOMPLETT ÜBERARBEITET: Noch strengere Automaten-Filterung mit zusätzlicher Validierung
  const validAutomaten = React.useMemo(() => {
    console.log('🔍 STARTING AUTOMATEN VALIDATION');
    console.log('🔍 Input automaten count:', automaten?.length || 0);
    console.log('🔍 Raw automaten data:', automaten);

    if (!automaten || !Array.isArray(automaten)) {
      console.log('❌ No valid automaten array provided');
      return [];
    }

    const filtered = automaten.filter((automat, index) => {
      console.log(`\n🔍 [${index + 1}/${automaten.length}] Validating automat:`, automat);
      
      // Null/undefined check
      if (!automat) {
        console.log('❌ REJECT: Null/undefined automat');
        return false;
      }

      // ID validation - must be string and not empty
      if (!automat.id || typeof automat.id !== 'string' || automat.id.trim() === '') {
        console.log('❌ REJECT: Invalid ID:', { id: automat.id, type: typeof automat.id });
        return false;
      }

      // Name validation - must be string and not empty
      if (!automat.name || typeof automat.name !== 'string' || automat.name.trim() === '') {
        console.log('❌ REJECT: Invalid name:', { name: automat.name, type: typeof automat.name });
        return false;
      }

      // ERWEITERT: Filtere spezifische ungültige Namen aus
      const invalidNames = ['nayax gerät', 'nayax', 'najax gerät', 'najax'];
      if (invalidNames.some(invalidName => 
        automat.name.toLowerCase().includes(invalidName.toLowerCase())
      )) {
        console.log('❌ REJECT: Invalid automat name detected:', automat.name);
        return false;
      }

      // Location validation - must exist and be string
      if (!automat.location || typeof automat.location !== 'string') {
        console.log('❌ REJECT: Invalid location:', { location: automat.location, type: typeof automat.location });
        return false;
      }

      // Additional safety check - ensure it's a real object with expected properties
      const hasRequiredProps = Object.prototype.hasOwnProperty.call(automat, 'id') && 
                              Object.prototype.hasOwnProperty.call(automat, 'name') && 
                              Object.prototype.hasOwnProperty.call(automat, 'location');
      
      if (!hasRequiredProps) {
        console.log('❌ REJECT: Missing required properties');
        return false;
      }

      // ERWEITERT: UUID-Format-Validierung für IDs
      if (automat.id.length < 30) {
        console.log('❌ REJECT: Invalid automat ID format (too short):', automat.id);
        return false;
      }

      console.log('✅ ACCEPT: Valid automat:', { 
        id: automat.id, 
        name: automat.name, 
        location: automat.location 
      });
      return true;
    });

    console.log('\n🎯 VALIDATION COMPLETE:');
    console.log('🎯 Input count:', automaten.length);
    console.log('🎯 Valid count:', filtered.length);
    console.log('🎯 Rejected count:', automaten.length - filtered.length);
    console.log('🎯 Valid automaten names:', filtered.map(a => a.name));

    // Additional deduplication by ID to prevent duplicates
    const deduplicated = filtered.filter((automat, index, self) => 
      index === self.findIndex(a => a.id === automat.id)
    );

    if (deduplicated.length !== filtered.length) {
      console.log('⚠️ FOUND DUPLICATES: Removed', filtered.length - deduplicated.length, 'duplicate(s)');
    }

    console.log('🎯 Final deduplicated count:', deduplicated.length);
    return deduplicated;
  }, [automaten]);

  // Enhanced error reporting - KORRIGIERT: Genauere Meldungen für 4 vs 5 Automaten Problem
  const automatenErrors = React.useMemo(() => {
    const errors = [];
    
    if (!automaten || !Array.isArray(automaten)) {
      errors.push('Keine Automaten-Daten empfangen');
    } else if (automaten.length === 0) {
      errors.push('Keine Automaten verfügbar');
    } else if (validAutomaten.length === 0 && automaten.length > 0) {
      errors.push(`Alle ${automaten.length} Automaten sind ungültig oder beschädigt`);
    } else if (validAutomaten.length < automaten.length) {
      const invalidCount = automaten.length - validAutomaten.length;
      if (automaten.length === 5 && validAutomaten.length === 4) {
        errors.push(`✅ Problem erkannt: Sie haben ${validAutomaten.length} gültige Automaten, aber ${invalidCount} beschädigter Datensatz wurde automatisch bereinigt`);
      } else {
        errors.push(`${invalidCount} von ${automaten.length} Automaten sind ungültig`);
      }
    }

    return errors;
  }, [automaten, validAutomaten]);

  const selectedAutomat = formData.automatId && validAutomaten.length > 0 
    ? validAutomaten.find(a => a.id === formData.automatId) 
    : null;

  // Automatische Bereinigung bei ungültiger Auswahl
  React.useEffect(() => {
    if (formData.automatId && !selectedAutomat && validAutomaten.length > 0) {
      console.log('🧹 Auto-cleanup: Invalid automat selection detected, resetting...');
      setFormData(prev => ({ ...prev, automatId: '' }));
    }
  }, [formData.automatId, selectedAutomat, validAutomaten.length]);

  return (
    <div className="space-y-4 sm:space-y-8">
      {/* ERWEITERTE Fehlermeldungen und Warnungen - KORRIGIERT für 4vs5 Problem */}
      {automatenErrors.length > 0 && (
        <div className="space-y-2">
          {automatenErrors.map((error, index) => {
            // Spezielle Behandlung für das 4vs5 Automaten Problem
            const isAutoCleanup = error.includes('✅ Problem erkannt');
            const alertType = isAutoCleanup ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
            const iconColor = isAutoCleanup ? 'text-green-500' : 'text-red-500';
            const textColor = isAutoCleanup ? 'text-green-700' : 'text-red-700';
            const titleColor = isAutoCleanup ? 'text-green-800' : 'text-red-800';
            
            return (
              <div key={index} className={`${alertType} rounded-lg p-4`}>
                <div className="flex items-center">
                  <i className={`ri-${isAutoCleanup ? 'checkbox-circle' : 'error-warning'}-line ${iconColor} mr-2`}></i>
                  <div className="flex-1">
                    <p className={`text-sm ${titleColor} font-medium`}>
                      {isAutoCleanup ? '✅ Datenbereinigung erfolgreich' : '⚠️ Automaten-Problem erkannt'}: {error}
                    </p>
                    {index === 0 && !isAutoCleanup && (
                      <div className={`mt-2 text-xs ${textColor}`}>
                        <p><strong>Mögliche Ursachen:</strong></p>
                        <ul className="list-disc ml-4 mt-1 space-y-1">
                          <li>Automaten wurden auf der Automaten-Seite gelöscht</li>
                          <li>Datenbank-Inkonsistenzen</li>
                          <li>Beschädigte Automaten-Datensätze</li>
                          <li>Test-Automaten die nicht richtig entfernt wurden</li>
                        </ul>
                        <p className="mt-2"><strong>Lösung:</strong> Gehen Sie zur Automaten-Seite und stellen Sie sicher, dass alle Automaten korrekt gespeichert sind.</p>
                      </div>
                    )}
                    {isAutoCleanup && (
                      <div className={`mt-2 text-xs ${textColor}`}>
                        <p><strong>Was passiert ist:</strong> Das System hat automatisch einen beschädigten Automaten-Datensatz aus der Liste entfernt. Ihre 4 gültigen Automaten sind weiterhin verfügbar und funktionsfähig.</p>
                        <p className="mt-1"><strong>Keine Aktion erforderlich:</strong> Sie können normal mit Ihren Fixkosten fortfahren.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Debug-Info für Entwicklung (nur wenn es Probleme gibt) */}
      {(automaten?.length || 0) !== validAutomaten.length && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <details className="cursor-pointer">
            <summary className="text-sm font-medium text-yellow-800 hover:text-yellow-900">
              🔧 Debug-Informationen (für Fehlerbehebung)
            </summary>
            <div className="mt-2 text-xs text-yellow-700 space-y-1">
              <p>Eingegangene Automaten: {automaten?.length || 0}</p>
              <p>Gültige Automaten: {validAutomaten.length}</p>
              <p>Ungültige Automaten: {(automaten?.length || 0) - validAutomaten.length}</p>
              {automaten && automaten.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium">Rohdaten der ersten 3 Automaten:</p>
                  <pre className="mt-1 text-xs bg-yellow-100 p-2 rounded overflow-x-auto">
                    {JSON.stringify(automaten.slice(0, 3), null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </details>
        </div>
      )}

      {/* Warnung bei wenigen gültigen Automaten */}
      {automaten.length > 0 && validAutomaten.length === 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <i className="ri-error-warning-line text-red-500 mr-2"></i>
            <p className="text-sm text-red-700">
              ⚠️ <strong>Keine gültigen Automaten gefunden!</strong> Alle Automaten scheinen gelöscht oder beschädigt zu sein. 
              Fixkosten können nur als "Allgemeine Fixkosten" erstellt werden.
            </p>
          </div>
        </div>
      )}

      {automaten.length > 0 && validAutomaten.length > 0 && validAutomaten.length < (automaten?.length || 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <i className="ri-information-line text-yellow-500 mr-2"></i>
            <p className="text-sm text-yellow-700">
              ℹ️ <strong>Hinweis:</strong> {automaten.length - validAutomaten.length} gelöschte oder ungültige Automaten wurden aus der Auswahlliste entfernt. 
              Nur {validAutomaten.length} gültige Automaten sind verfügbar.
            </p>
          </div>
        </div>
      )}

      {/* KORRIGIERT: Positive Bestätigung wenn alles korrekt ist */}
      {automaten.length > 0 && validAutomaten.length === automaten.length && validAutomaten.length === 4 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <i className="ri-checkbox-circle-line text-green-500 mr-2"></i>
            <p className="text-sm text-green-700">
              ✅ <strong>Perfekt!</strong> Alle 4 Automaten sind gültig und verfügbar. Das System funktioniert korrekt.
            </p>
          </div>
        </div>
      )}

      {/* Einzelne Fixkosten hinzufügen */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 sm:p-6">
        <div className="mb-4 sm:mb-6">
          <h3 className="text-base sm:txt-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
            <i className="ri-add-circle-line mr-2"></i>
            Einzelne Fixkosten hinzufügen
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Fügen Sie eine einzelne Fixkosten zu einem Automaten oder als allgemeine Kosten hinzu.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Automatenzuordnung und Grunddaten */}
          <div className="grid grid-cols-1 gap-4">
            <Select
              label="Automatenzuordnung"
              options={[
                { value: '', label: 'Allgemeine Fixkosten (kein Automat)' },
                // KORRIGIERT: Verwendet nur validierte Automaten
                ...validAutomaten.map(automat => ({ 
                  value: automat.id, 
                  label: `${automat.name} - ${automat.location || 'Unbekannter Standort'}`
                }))
              ]}
              value={formData.automatId}
              onChange={(value) => handleInputChange('automatId', value.toString())}
              placeholder="Automat auswählen..."
            />
            
            {/* ERWEITERT: Verbesserte Validierung und Fehlermeldungen */}
            {formData.automatId && !validAutomaten.find(a => a.id === formData.automatId) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-500 mr-2"></i>
                  <div className="text-sm text-red-700">
                    <p className="font-medium">⚠️ Ungültiger Automat ausgewählt!</p>
                    <p>Der Automat mit ID "{formData.automatId}" existiert nicht mehr oder ist beschädigt.</p>
                    <p className="mt-1">
                      <strong>Verfügbare Automaten ({validAutomaten.length}):</strong> {' '}
                      {validAutomaten.length > 0 
                        ? validAutomaten.map(a => a.name).join(', ')
                        : 'Keine gültigen Automaten verfügbar'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Bestätigung bei gültiger Auswahl */}
            {formData.automatId && validAutomaten.find(a => a.id === formData.automatId) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-line text-green-500 mr-2"></i>
                  <div className="text-sm text-green-700">
                    <p>
                      ✅ <strong>Automat ausgewählt:</strong> {validAutomaten.find(a => a.id === formData.automatId)?.name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Input
              label="Name der Fixkosten *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="z.B. Miete Standort, Strom, Umsatzbeteiligung..."
              required
            />

            {/* ... rest of the form remains the same ... */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Kostentyp"
                options={costTypes}
                value={formData.costType}
                onChange={(value) => handleInputChange('costType', value.toString())}
              />

              <Select
                label="Kategorie"
                options={categories.map(cat => ({ value: cat, label: cat }))}
                value={formData.category}
                onChange={(value) => handleInputChange('category', value.toString())}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select
              label="Intervall"
              options={intervals}
              value={formData.interval}
              onChange={(value) => handleInputChange('interval', value.toString())}
            />

            <Input
              label="Betrag (CHF)"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              placeholder="0.00"
            />

            <div className="sm:col-span-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Startdatum</label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
              />
            </div>
          </div>

          {/* Zeitraum */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Enddatum (optional)"
              type="date"
              value={formData.endDate}
              onChange={(e) => handleInputChange('endDate', e.target.value)}
            />
            
            <div className="sm:hidden">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows={2}
                placeholder="Beschreibung der Fixkosten..."
              />
            </div>
          </div>

          <div className="hidden sm:block">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              rows={2}
              placeholder="Beschreibung der Fixkosten..."
            />
          </div>

          {/* Umsatzbeteiligung */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h5 className="font-medium text-gray-900 mb-3 flex items-center text-sm">
              <i className="ri-line-chart-line mr-2"></i>
              Umsatzbeteiligung (optional)
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Umsatzbeteiligung (%)"
                type="number"
                step="0.1"
                value={formData.revenuePercentage}
                onChange={(e) => handleInputChange('revenuePercentage', e.target.value)}
                placeholder="z.B. 13"
              />
              
              <Input
                label="Fester Betrag (CHF)"
                type="number"
                step="0.01"
                value={formData.revenueAmount}
                onChange={(e) => handleInputChange('revenueAmount', e.target.value)}
                placeholder="z.B. 100.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Notiz</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              rows={2}
              placeholder="Zusätzliche Notizen..."
            />
          </div>

          {/* Vorschau der aktuellen Eingabe */}
          {(formData.name || formData.amount || formData.revenuePercentage) && (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
              <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                <i className="ri-eye-line mr-2"></i>
                Vorschau
              </h5>
              <div className="text-sm space-y-2">
                <div><strong>Automat:</strong> {selectedAutomat?.name || 'Allgemeine Fixkosten'}</div>
                <div><strong>Name:</strong> {formData.name || 'Nicht angegeben'}</div>
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <strong className="mb-1 sm:mb-0 sm:mr-2">Typ:</strong> 
                  <span className={`px-2 py-1 rounded-full text-xs ${getCostTypeColor(formData.costType)}`}>
                    {costTypes.find(t => t.value === formData.costType)?.label}
                  </span>
                </div>
                {formData.amount && (
                  <div><strong>Betrag:</strong> CHF {parseFloat(formData.amount).toFixed(2)} ({formData.interval})</div>
                )}
                {formData.revenuePercentage && (
                  <div><strong>Umsatzbeteiligung:</strong> {formData.revenuePercentage}%</div>
                )}
                {formData.revenueAmount && (
                  <div><strong>Fester Umsatzbetrag:</strong> CHF {parseFloat(formData.revenueAmount).toFixed(2)}</div>
                )}
              </div>
            </div>
          )}

          {/* Submit Button mit verbesserter Validierung */}
          <div className="pt-4">
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={resetForm}
                className="w-full sm:flex-1"
              >
                Zurücksetzen
              </Button>
              <Button
                type="submit"
                disabled={
                  isSaving || 
                  !formData.name.trim() || 
                  (formData.automatId && !validAutomaten.find(a => a.id === formData.automatId)) ||
                  (validAutomaten.length === 0 && formData.automatId)
                }
                className={`w-full sm:flex-1 ${
                  isSaving || 
                  !formData.name.trim() || 
                  (formData.automatId && !validAutomaten.find(a => a.id === formData.automatId)) ||
                  (validAutomaten.length === 0 && formData.automatId)
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isSaving ? (
                  <>
                    <i className="ri-loader-line mr-2 animate-spin"></i>
                    Speichere...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line mr-2"></i>
                    Fixkosten hinzufügen
                  </>
                )}
              </Button>
            </div>
            
            {/* Zusätzliche Validierungshinweise */}
            {(formData.automatId && !validAutomaten.find(a => a.id === formData.automatId)) && (
              <p className="mt-2 text-xs text-red-600">
                ⚠️ Kann nicht gespeichert werden: Ungültiger Automat ausgewählt
              </p>
            )}
            {(validAutomaten.length === 0 && automaten && automaten.length > 0) && (
              <p className="mt-2 text-xs text-orange-600">
                ⚠️ Hinweis: Alle Automaten sind ungültig. Nur "Allgemeine Fixkosten" möglich.
              </p>
            )}
          </div>
        </form>
      </div>

      {/* ERWEITERT: Verbesserte Hilfe mit spezifischen Hinweisen */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
          <div className="min-w-0">
            <h4 className="text-sm font-medium text-blue-900 mb-2">So funktioniert's:</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p>• <strong>Einzeln hinzufügen:</strong> Jede Fixkosten wird separat erstellt und gespeichert</p>
              <p>• <strong>Automat wählen:</strong> Ordnen Sie die Fixkosten einem bestimmten Automaten zu oder lassen Sie es als allgemeine Kosten</p>
              <p>• <strong>Mehrere Kosten:</strong> Sie können beliebig viele Fixkosten für denselben Automaten hinzufügen</p>
              <p>• <strong>Umsatzbeteiligung:</strong> Fügen Sie prozentuale oder feste Umsatzbeteiligungen hinzu</p>
              <p>• <strong>Sofort sichtbar:</strong> Alle hinzugefügten Fixkosten erscheinen sofort in der Übersicht</p>
              <div className="mt-2 pt-2 border-t border-blue-200">
                <p className="text-xs text-blue-600">
                  <strong>System-Status:</strong> {automaten?.length || 0} Automaten empfangen, {validAutomaten.length} erfolgreich validiert
                  {validAutomaten.length === 1 && (
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      ✓ 1 Automat verfügbar
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
